import React, { useState, useRef, useEffect } from 'react';
import { Search, Check } from 'lucide-react';
import { FaChevronDown } from 'react-icons/fa';
import { motion, AnimatePresence } from 'framer-motion';

function CustomSelect({ value, onChange, options, placeholder, width = 'w-56' }) {
  const [isOpen, setIsOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const handleClickOutside = e => {
      if (ref.current && !ref.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const selectedOption = options.find(opt => opt.value === value);

  return (
    <div ref={ref} className={`relative ${width}`}>
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="w-full px-3 pr-10 py-2 border border-gray-300 rounded-md bg-white text-left focus:outline-none focus:ring-2 focus:ring-emerald-500 transition-all"
      >
        <span className={value === 'all' ? 'text-gray-500' : 'text-gray-900'}>
          {selectedOption?.label || placeholder}
        </span>
        <FaChevronDown
          className={`absolute right-3 top-1/2 -translate-y-1/2 w-3 h-3 text-gray-400 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`}
        />
      </button>

      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.15 }}
            className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-md shadow-lg overflow-hidden"
          >
            {options.map(opt => (
              <button
                key={opt.value}
                type="button"
                onClick={() => {
                  onChange(opt.value);
                  setIsOpen(false);
                }}
                className={`w-full px-3 py-2 text-left flex items-center justify-between hover:bg-gray-50 transition-colors ${
                  value === opt.value ? 'bg-emerald-50 text-emerald-700' : 'text-gray-700'
                }`}
              >
                <span>{opt.label}</span>
                {value === opt.value && <Check className="w-4 h-4 text-emerald-600" />}
              </button>
            ))}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

export default function EmployeeFilters({
  searchTerm,
  setSearchTerm,
  filterDept,
  setFilterDept,
  filterStatus,
  setFilterStatus,
  departments,
}) {
  const deptOptions = [
    { value: 'all', label: 'Semua Departemen' },
    ...departments.map(dept => ({ value: dept.id, label: dept.name })),
  ];

  const statusOptions = [
    { value: 'all', label: 'Semua Status' },
    { value: 'active', label: 'Aktif' },
    { value: 'inactive', label: 'Tidak Aktif' },
    { value: 'terminated', label: 'Berhenti' },
  ];

  return (
    <div className="bg-white rounded-lg border shadow-sm p-4">
      <div className="flex flex-col md:flex-row gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="text"
            placeholder="Cari nama, NIP, atau email..."
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
          />
        </div>
        <CustomSelect
          value={filterDept}
          onChange={setFilterDept}
          options={deptOptions}
          placeholder="Semua Departemen"
          width="w-full md:w-56"
        />
        <CustomSelect
          value={filterStatus}
          onChange={setFilterStatus}
          options={statusOptions}
          placeholder="Semua Status"
          width="w-full md:w-44"
        />
      </div>
    </div>
  );
}
